<template>
  <div class="h-full overflow-hidden">
    <BaseTable
      table-id="price-policy-customers"
      :list="tableData"
      :total-records="tableDataCount"
      :headers="customerHeaders"
      :isAddNewHidden="mode == 'view'"
      :hideMultiselectDelete="mode == 'view'"
      :items-per-page="10"
      :selected-page="selectedPage"
      is-add-all-button-active
      hideGrouping
      @refresh-data="onRefreshTableData"
      @add-new="onAddCustomers"
      @add-all="modalAddAllActive = true"
      @delete-multiselect="onDeleteMultiSelect">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="!item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="onDeleteSingle(item)" />
        </div>
      </template>
    </BaseTable>
    <PriceCatalogsOrderListCustomersModalAdd
      v-if="isModalAddActive"
      :list="customersList"
      :total-records="customersListCount"
      @close="isModalAddActive = false"
      @refresh-data="onAddCustomers"
      @multi-select="onMultiselectCustomers" />

    <BaseModalConfirm
      v-if="modalDeleteConfirmActive"
      @cancel="modalDeleteConfirmActive = false"
      @confirm="onDeleteSingleConfirm()" />

    <BaseModalConfirm
      v-if="modalAddAllActive"
      @cancel="modalAddAllActive = false"
      @confirm="onAddAllCustomers">
      <template #title>{{ $t('common.actions.addAll') }}</template>
      <template #text>{{ $t('price-catalog.addAllCustomers') }}</template>
      <template #buttonText>{{ $t('common.actions.create') }}</template>
    </BaseModalConfirm>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { getCustomersWithConditions } from '@/services/api/traders';

import BaseTable from '../common/BaseTable.vue';
import ActionButton from '../common/ActionButton.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import PriceCatalogsOrderListCustomersModalAdd from './PriceCatalogsOrderListCustomersModalAdd.vue';
import type { Customer } from '@/types/Traders';
import { RecordMode, type TableFilterData } from '@/types/Base';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';

const { t: $t } = useI18n({ useScope: 'global' });
const customerHeaders = computed(() => [
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  { value: 'profession', text: `${$t('customer.profession')}`, type: 'text', active: true },
  { value: 'trader.name', text: `${$t('customer.traderName')}`, type: 'text', active: true },
  { value: 'trader.code', text: `${$t('customer.traderCode')}`, type: 'text', active: true },
  {
    value: 'trader.taxNumber',
    text: `${$t('customer.traderTaxNumber')}`,
    type: 'text',
    active: true,
  },
  { value: 'trader.address', text: `${$t('address')}`, type: 'text', active: true },
  { value: 'trader.zipCode', text: `${$t('zipCodes')}`, type: 'text', active: true },
  {
    value: 'defaultPaymentType.name',
    text: 'Default Payment Type',
    type: 'text',
    active: true,
  },
  { value: 'actions', text: 'Actions', active: props.mode == 'edit' },
]);

const props = defineProps<{
  mode: 'view' | 'edit';
  tableData: Customer[];
  tableDataCount: number;
  selectedPage: number;
  addedCustomers: Customer[];
}>();

const emit = defineEmits([
  'refreshTableData',
  'addCustomers',
  'deleteCustomers',
  'addAllCustomers',
]);

async function onRefreshTableData(data: TableFilterData) {
  data.sortColumn = data.column != null ? customerHeaders.value[data.column].value : '';
  data.sortAscending = data?.order === 'asc';
  emit('refreshTableData', data);
}

const isModalAddActive = ref(false);
const customersList = ref<Customer[]>([]);
const customersListCount = ref<number>();

async function onAddCustomers(data: TableFilterData | undefined) {
  const res = await getCustomersWithConditions({
    itemsPerPage: data?.itemsPerPage || 50,
    query: data?.query,
    page: data?.page || 1,
    sortColumn: data?.sortColumn || '',
    sortAscending: data?.order === 'asc',
    withoutPricingPolicy: true,
  });
  if (res) {
    const customerIds = new Set(props.addedCustomers.map((item) => item.id));
    const filteredCustomers = res.data.filter((item) => !customerIds.has(item.id));
    customersList.value = filteredCustomers;
    customersListCount.value = res.count;
  }
  isModalAddActive.value = true;
}

async function onMultiselectCustomers(items: Customer[]) {
  isModalAddActive.value = false;
  items.forEach((item) => {
    item.recordMode = RecordMode.Added;
  });
  emit('addCustomers', items);
}

const modalDeleteConfirmActive = ref(false);
const customerToDelete = ref<Customer>();
function onDeleteSingle(item: Customer) {
  customerToDelete.value = item;
  modalDeleteConfirmActive.value = true;
}

function onDeleteSingleConfirm() {
  if (!customerToDelete.value) return;
  customerToDelete.value.recordMode = RecordMode.Deleted;
  emit('deleteCustomers', [customerToDelete.value]);
  modalDeleteConfirmActive.value = false;
}

const modalAddAllActive = ref(false);
function onAddAllCustomers() {
  emit('addAllCustomers');
  modalAddAllActive.value = false;
}

function onDeleteMultiSelect(customers: Customer[]) {
  customers.forEach((customer) => {
    customer.recordMode = RecordMode.Deleted;
  });
  emit('deleteCustomers', customers);
}
</script>
